import {
  Box,
  Button,
  CircularProgress,
  Icon,
  IconButton,
  Paper,
  Snackbar,
  Tooltip,
  Typography,
} from "@material-ui/core"
import { Alert, AlertTitle } from "@material-ui/lab"
import { FileUploadDialog } from "components"
import { ROOT_TITLE, uploadReceivedDocumentsService } from "lib"
import { uploadLegderDataService } from "lib/utils/api/ledgerSummary"
import React from "react"
import { Helmet } from "react-helmet"
import { IMessageConfig } from "types"

type TypeFile = "arFile" | "ruFile" | null

const ClientRetentionPage = () => {
  const [messageConfig, setMessageConfig] = React.useState<IMessageConfig>({
    open: false,
    message: "",
    severity: "info",
  })
  const [arFile, setFile1] = React.useState<File | null>(null)
  const [ruFile, setFile2] = React.useState<File | null>(null)
  const [open, setOpen] = React.useState<boolean>(false)
  const [loading, setLoading] = React.useState<boolean>(false)
  const [typeFile, setTypeFile] = React.useState<TypeFile>(null)

  // Estados para Libro Contable
  const [ledgerFile, setLedgerFile] = React.useState<File | null>(null)
  const [openLedger, setOpenLedger] = React.useState<boolean>(false)
  const [loadingLedger, setLoadingLedger] = React.useState<boolean>(false)

  const existFileOne = React.useMemo(() => {
    return Boolean(arFile)
  }, [arFile])

  const existFileTwo = React.useMemo(() => {
    return Boolean(ruFile)
  }, [ruFile])

  const existBothFiles = React.useMemo(() => {
    return existFileOne && existFileTwo
  }, [existFileOne, existFileTwo])

  const existLedgerFileMemo = React.useMemo(() => {
    return Boolean(ledgerFile)
  }, [ledgerFile])

  const handleDeleteFile = (typeFile: TypeFile) => {
    if (typeFile === "arFile") {
      setFile1(null)
    }
    if (typeFile === "ruFile") {
      setFile2(null)
    }
  }

  const handleDeleteLedgerFile = () => {
    setLedgerFile(null)
  }

  const handleUploadFiles = async () => {
    try {
      if (arFile === null && ruFile === null) {
        setMessageConfig({
          open: true,
          message: "Debe subir al menos un archivo",
          severity: "error",
        })
        return
      }
      setLoading(true)
      const status = await uploadReceivedDocumentsService(arFile, ruFile)
      if (status === 200) {
        setMessageConfig({
          open: true,
          message: "Los archivos subidos se procesarán a continuación",
          severity: "info",
        })
        setFile1(null)
        setFile2(null)
      }
      if (status === 409) {
        setMessageConfig({
          open: true,
          message:
            "Existe en este momento un archivo procesándose, inténtelo mas tarde....",
          severity: "warning",
        })
        return
      }
    } catch (error: any) {
      setLoading(false)
      setMessageConfig({ open: true, message: error, severity: "error" })
    } finally {
      setLoading(false)
    }
  }

  const handleUploadLedgerFile = async () => {
    if (!ledgerFile) {
      return setMessageConfig({
        open: true,
        message: "Seleccione un archivo",
        severity: "error",
      })
    }

    setLoadingLedger(true)
    try {
      const res = await uploadLegderDataService(ledgerFile)
      setMessageConfig({
        open: true,
        message: res.message,
        severity: "success",
      })
      setLedgerFile(null)
    } catch (error: any) {
      const { data, status } = error.response || {}
      setMessageConfig({
        open: true,
        message: data?.message || "Error al procesar el archivo",
        severity: status === 409 ? "warning" : "error",
      })
    } finally {
      setLoadingLedger(false)
    }
  }

  const handleUpload = async (file: File) => {
    try {
      if (typeFile === "arFile") {
        const arFile = file
        setFile1(arFile)
      } else if (typeFile === "ruFile") {
        const ruFile = file
        setFile2(ruFile)
      }
    } catch (error) {
      console.log("Error", error)
    }
  }

  const handleUploadLedgerAccept = async (file: File) => {
    try {
      setLedgerFile(file)
    } catch (error) {
      console.log("Error", error)
    }
  }

  return (
    <Paper className="flex flex-col h-full overflow-hidden p-4">
      <Helmet>
        <title>{`${ROOT_TITLE} - Carga Excels`}</title>
      </Helmet>

      <Box className="ml-1.5 pt-2" width={580}>
        {/* ALERTA */}
        <Alert
          severity="warning"
          icon={<Icon fontSize="inherit">info</Icon>}
          style={{
            marginBottom: 20,
            borderRadius: 8,
            alignItems: "center",
          }}
        >
          <AlertTitle>Importante</AlertTitle>
          <strong>No</strong> es obligatorio que los archivos se carguen juntos,
          pero es importante tener en cuenta que el archivo <strong>RU</strong>{" "}
          es dependiente de que el archivo <strong>AR</strong> se haya procesado
          previamente.
          <br />
        </Alert>

        {/* BOTONES DE SUBIDA */}
        <Box style={{ display: "flex", gap: 8, marginBottom: 3 }}>
          {!existFileOne && (
            <Button
              fullWidth
              disableElevation
              color="primary"
              size="small"
              variant="contained"
              onClick={() => {
                setTypeFile("arFile")
                setOpen(true)
              }}
              style={{ height: 38 }}
            >
              Subir Excel AR
              <Icon fontSize="small" className="ml-1">
                upload
              </Icon>
            </Button>
          )}

          {!existFileTwo && (
            <Button
              fullWidth
              disableElevation
              color="primary"
              size="small"
              variant="contained"
              onClick={() => {
                setTypeFile("ruFile")
                setOpen(true)
              }}
              style={{ height: 38 }}
            >
              Subir Excel RU
              <Icon fontSize="small" className="ml-1">
                upload
              </Icon>
            </Button>
          )}
        </Box>

        {/* LISTA DE ARCHIVOS */}
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            marginBottom: 3,
          }}
        >
          {existFileOne && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={1.2}
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                backgroundColor: "#fafafa",
              }}
            >
              <Typography variant="body2">
                <strong>Archivo AR:</strong> {arFile!.name}
              </Typography>

              <Tooltip title="Eliminar archivo">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteFile("arFile")}
                >
                  <Icon fontSize="small" color="error">
                    close
                  </Icon>
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {existFileTwo && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={1.2}
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                backgroundColor: "#fafafa",
              }}
            >
              <Typography variant="body2">
                <strong>Archivo RU:</strong> {ruFile!.name}
              </Typography>

              <Tooltip title="Eliminar archivo">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteFile("ruFile")}
                >
                  <Icon fontSize="small" color="error">
                    close
                  </Icon>
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* BOTÓN PROCESAR */}
        <Box mt={3}>
          <Button
            fullWidth
            disabled={(!existFileOne && !existFileTwo) || loading}
            disableElevation
            color="primary"
            size="small"
            variant="contained"
            onClick={handleUploadFiles}
            style={{ height: 40 }}
          >
            {loading ? (
              <CircularProgress
                size={16}
                thickness={6}
                style={{ color: "white" }}
              />
            ) : existBothFiles ? (
              "Procesar archivos"
            ) : (
              "Procesar archivo"
            )}
            <Icon fontSize="small" className="ml-1">
              cloud_upload
            </Icon>
          </Button>
        </Box>

        {/* DIVISOR */}
        <Box style={{ borderTop: "1px solid #e0e0e0", margin: "24px 0" }} />
        <Typography
          variant="subtitle2"
          style={{ marginBottom: 8, fontWeight: "bold" }}
        >
          Libro Contable
        </Typography>

        <Alert
          severity="warning"
          icon={<Icon fontSize="inherit">info</Icon>}
          style={{
            marginBottom: 20,
            borderRadius: 8,
            alignItems: "center",
          }}
        >
          <AlertTitle>Importante</AlertTitle>
          <strong>El Libro Contable</strong> se procesa de forma independiente.
          <strong> Asegúrese</strong> de que el archivo tenga el formato
          correcto antes de subirlo
          <br />
        </Alert>

        {/* BOTÓN SUBIR LIBRO CONTABLE */}
        <Box style={{ display: "flex", gap: 8, marginBottom: 3 }}>
          {!existLedgerFileMemo && (
            <Button
              fullWidth
              disableElevation
              color="secondary"
              size="small"
              variant="contained"
              onClick={() => setOpenLedger(true)}
              style={{ height: 38, background: "#ffb300" }}
            >
              Subir Libro Contable
              <Icon fontSize="small" className="ml-1">
                upload
              </Icon>
            </Button>
          )}
        </Box>

        {/* LISTA ARCHIVO LIBRO CONTABLE */}
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 10,
            marginBottom: 3,
          }}
        >
          {existLedgerFileMemo && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={1.2}
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                backgroundColor: "#fafafa",
              }}
            >
              <Typography variant="body2">
                <strong>Libro Contable:</strong> {ledgerFile!.name}
              </Typography>

              <Tooltip title="Eliminar archivo">
                <IconButton size="small" onClick={handleDeleteLedgerFile}>
                  <Icon fontSize="small" color="error">
                    close
                  </Icon>
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* BOTÓN PROCESAR LIBRO CONTABLE */}
        <Box mt={2}>
          <Button
            fullWidth
            disabled={!existLedgerFileMemo || loadingLedger}
            disableElevation
            color="secondary"
            size="small"
            variant="contained"
            onClick={handleUploadLedgerFile}
            style={{ height: 40 }}
          >
            {loadingLedger ? (
              <CircularProgress
                size={16}
                thickness={6}
                style={{ color: "white" }}
              />
            ) : (
              "Procesar Libro Contable"
            )}
            <Icon fontSize="small" className="ml-1">
              cloud_upload
            </Icon>
          </Button>
        </Box>

        {/* SNACKBAR */}
        <Snackbar
          open={messageConfig.open}
          autoHideDuration={6000}
          onClose={() => setMessageConfig({ ...messageConfig, open: false })}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        >
          <Alert
            variant="filled"
            onClose={() => setMessageConfig({ ...messageConfig, open: false })}
            severity={messageConfig.severity}
          >
            {messageConfig.message}
          </Alert>
        </Snackbar>

        {/* MODAL SUBIDA AR/RU */}
        <FileUploadDialog
          open={open}
          onClose={() => setOpen(false)}
          onAccept={(files) => handleUpload(files[0].file)}
          singleFile
          hideDescription
          validateisExel
        />

        {/* MODAL SUBIDA LIBRO CONTABLE */}
        <FileUploadDialog
          open={openLedger}
          onClose={() => setOpenLedger(false)}
          onAccept={(files) => handleUploadLedgerAccept(files[0].file)}
          singleFile
          hideDescription
          validateisExel
        />
      </Box>
    </Paper>
  )
}

export default ClientRetentionPage
